/**
 * TrustedEcosystemSection — Premium ecosystem positioning.
 *
 * Displays the approved NinjaTrader® wordmark as a single centered card
 * with the official affiliate/introducing-partner link.
 *
 * Compliance:
 *  - Uses local brand-approved logo file (no external CDN fetches)
 *  - Wordmark framed in a dark card mirroring brand context
 *  - Trademark notice covers NinjaTrader
 *  - No "Official Partner" / endorsement wording
 */
import React from "react";

const EcosystemCard = ({ href, alt, src, height, dataTestId, badge }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer sponsored"
    className="group relative flex items-center justify-center px-8 sm:px-10 py-7 rounded-2xl bg-slate-950 hover:bg-black border border-white/10 hover:border-white/20 transition-all duration-200 hover:-translate-y-0.5 shadow-sm hover:shadow-lg"
    data-testid={dataTestId}
    aria-label={`Visit ${alt}`}
  >
    {badge && (
      <span className="absolute top-3 right-3 text-[9px] font-bold tracking-[0.15em] text-slate-500 uppercase">
        {badge}
      </span>
    )}
    <img
      src={src}
      alt={alt}
      style={{ height }}
      className="w-auto select-none pointer-events-none"
      loading="lazy"
      decoding="async"
    />
  </a>
);

export const TrustedEcosystemSection = () => {
  return (
    <section
      className="border-y border-slate-200 dark:border-white/[0.06] bg-slate-50/40 dark:bg-slate-950/40"
      data-testid="trusted-ecosystem-section"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        {/* Header */}
        <div className="flex flex-col items-start sm:items-center text-left sm:text-center max-w-2xl mx-auto mb-10 sm:mb-12">
          <p className="text-[10px] sm:text-[11px] font-bold tracking-[0.18em] text-slate-500 dark:text-slate-400 uppercase mb-3">
            Integrated Futures Trading Ecosystem
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white tracking-tight">
            Trusted Futures Trading Ecosystem
          </h2>
          <p className="mt-3 text-sm sm:text-base text-slate-600 dark:text-slate-300 leading-relaxed">
            FuturesFury provides analytics, educational tools, and trader
            intelligence for active futures traders using leading futures
            trading platforms and prop firms.
          </p>
        </div>

        {/* Ecosystem partner — single centered card */}
        <div className="flex justify-center">
          <div className="w-full max-w-md">
            <EcosystemCard
              href="https://ninjatraderus.pxf.io/AgjaKx"
              src="/partners/ninjatrader-wordmark.png"
              alt="NinjaTrader"
              height="40px"
              dataTestId="ecosystem-card-ninjatrader"
              badge="Platform"
            />
          </div>
        </div>

        {/* Legal disclaimer — muted */}
        <p
          className="mt-10 sm:mt-12 text-[11px] leading-relaxed text-slate-500 dark:text-slate-500 text-center max-w-2xl mx-auto"
          data-testid="ecosystem-trademark-notice"
        >
          NinjaTrader<sup className="text-[8px]">®</sup> is a registered
          trademark of NinjaTrader Group, LLC. FuturesFury is an
          independent platform and is not owned or operated by NinjaTrader.
        </p>
      </div>
    </section>
  );
};

export default TrustedEcosystemSection;
